import getLogger from '../logging';

const log = getLogger('action-on-match');

/**
 * @module action-on-match
 *
 * This is a collection of functions that provide a syntactic alternative to
 * nested switch statements and if-else blocks.
 */

/**
 * A predicate function that controls whether a branch of the processing tree
 * should run.
 */
export type Predicate<T> = (m: T) => boolean;

/**
 * An action is a function that is applied to a matched object.
 */
export type Action<T> = (m: T) => void;

/**
 * A two-tuple of a predicate and an action.
 */
export type MatchPair<T> = [Predicate<T>, Action<T>];

/**
 * Constructs a two-tuple consisting of a predicate and an action.
 */
export const match = <T>(p: Predicate<T>, a: Action<T>): MatchPair<T> => [p, a];

/**
 * A constructs a match pair with a predicate that always succeeds. This is
 * useful when nesting `firstof` and `anyof` match sequences.
 */
export const always = <T>(a: Action<T>): MatchPair<T> => [() => true, a];

export const trace = <T>(fn: Action<T>): MatchPair<T> => [
    (item: T) => {
        try {
            fn(item);
        } catch(error) {
            log.error('Error in trace function', error);
        }
        return false;
    }, () => {}
    ];

type MatchedPropertyValue = string | RegExp | null

/**
 * A useful predicate that checks if the predicate argument has a property
 * value that matches the given string or regular expression.
 */
export const prop = <T>(name: string, value: MatchedPropertyValue): Predicate<T> => {
    if(value == null) {
        return (t: T) => t[name] == null;
    }
    else if(typeof value == 'string') {
        return (t: T) => t[name] == value;
    } else if(value instanceof RegExp) {
        return (t: T) => value.test(t[name]);
    }
}

type Constructor<T> = { new(... args: unknown[]): T };

/**
 * Constructs a predicate that tests if the argument is an instance of the
 * class.
 */
export const isInstanceOf = <T>(ctor: Constructor<T>): Predicate<T> => {
    return (m: T) => m instanceof ctor;
}

/**
 * The `doall` constructor yields a new action that does all of the actions.
 */
export const doall = <T>(...actions: Action<T>[]): Action<T> => {
    return (item: T) => {
        for(const action of actions) {
            try {
                action(item);
            } catch(error) {
                log.error('Error during action application (doall)', error);            
            }
        }
    }
};
        

/**
 * The `firstof` constructor creates a new action that tests each given
 * predicate-action pair and applies the action of the first predicate to be
 * satisfied.
 */
export const firstof = (...pairs: MatchPair<any>[]): Action<any> => {
    return (item: any) => {
        for(const [predicate, action] of pairs) {
            try {
                if(predicate(item)) {
                    try {
                        action(item);
                        return;
                    } catch(error) {
                        log.error('Error during action application (firstof)', error);
                    }
                }
            } catch(error) {
                log.error('Error during predicate evaluation (firstof)', error);
            }
        }
    }
};

/**
 * The `anyof` constructor creates a new action that applies the corresponding
 * action for all matching predicates.
 */
export const anyof = <T>(...pairs: MatchPair<T>[]): Action<T> => {
    return (item: T) => {
        for(const [predicate, action] of pairs) {
            try {
                if(predicate(item)) {
                    try {
                        action(item);
                    } catch(error) {
                        log.error('Error during action application (anyof)', error);
                    }
                }
            } catch(error) {
                log.error('Error during predicate evaluation (anyof)', error);
            }
        }
    }
};

